// @ts-nocheck
(() => {
  const SELECTOR = '[data-doodle-bg]';
  const ASSET_SELECTOR = '[data-doodle-assets] [data-doodle-asset-url]';
  const DRAWABLE_SELECTOR = 'path, line, polyline, polygon, circle, ellipse, rect';

  const EDGE_BAND = 0.2;
  const SAFE_MARGIN_PX = 18;
  const MOBILE_DOODLE_MIN_WIDTH_PX = 10;
  const MOBILE_DOODLE_MAX_WIDTH_PX = 40;
  const CENTER_GUARD = {
    minX: 0.34,
    maxX: 0.66,
    minY: 0.28,
    maxY: 0.72,
  };

  const STYLE_CONFIG = {
    classic: {
      drawMsPerPath: 360,
      minDrawMs: 1500,
      maxDrawMs: 5200,
      holdMs: 2600,
      fadeMs: 700,
      minScale: 0.55,
      maxScale: 0.95,
      minGapMs: 360,
      maxGapMs: 820,
      maxActive: 8,
      maxActiveMobile: 4,
      strictNoOverlap: true,
      overlapGapPx: 16,
    },
    'edge-sketch': {
      drawMsPerPath: 460,
      minDrawMs: 1800,
      maxDrawMs: 6200,
      holdMs: 3000,
      fadeMs: 750,
      minScale: 0.7,
      maxScale: 1.15,
      minGapMs: 420,
      maxGapMs: 920,
      maxActive: 12,
      maxActiveMobile: 4,
    },
  };

  const SVG_CACHE = new Map();
  const doodleManagers = new WeakMap();
  const CLASSIC_ASSET_OVERRIDES = {
    'balloon.svg': { sizeMultiplier: 0.26, minRotationDeg: -16, maxRotationDeg: 16 },
    'lightbulb.svg': { sizeMultiplier: 0.28, minRotationDeg: -18, maxRotationDeg: 18 },
    'rocket.svg': { sizeMultiplier: 0.3, minRotationDeg: -22, maxRotationDeg: 22 },
  };

  // Cached MediaQueryList — created once at IIFE boot, not on every tick.
  // Previously `window.matchMedia('(max-width: 749px)')` was called inside
  // getCurrentMaxActive() and spawnOne(), both of which run on every cycle.
  const IS_MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function pickRandom(list) {
    return list[Math.floor(Math.random() * list.length)];
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function getStyleConfig(style) {
    return STYLE_CONFIG[style] || STYLE_CONFIG['edge-sketch'];
  }

  function getDoodleStrokeColor(container, style) {
    if (style !== 'edge-sketch' || !(container instanceof HTMLElement)) return null;

    const rgb = container.dataset.doodleStrokeRgb;
    if (!rgb) return null;

    const opacity = container.dataset.doodleStrokeOpacity;
    const resolvedOpacity = opacity !== undefined && opacity !== '' ? opacity : '1';
    return `rgb(${rgb} / ${resolvedOpacity})`;
  }

  function getAssetFileName(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const parts = parsed.pathname.split('/');
      return parts[parts.length - 1] || '';
    } catch (error) {
      const parts = String(url || '').split('/');
      return parts[parts.length - 1] || '';
    }
  }

  function getClassicAssetOverride(style, assetUrl) {
    if (style !== 'classic') return null;
    return CLASSIC_ASSET_OVERRIDES[getAssetFileName(assetUrl)] || null;
  }

  async function loadSvg(url) {
    if (SVG_CACHE.has(url)) {
      return SVG_CACHE.get(url);
    }

    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch doodle asset: ${url}`);
        }
        return response.text();
      })
      .then((svgText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svg = doc.querySelector('svg');
        if (!svg) {
          throw new Error(`No <svg> in doodle asset: ${url}`);
        }
        return svg;
      })
      .catch((error) => {
        console.warn(error);
        return null;
      });

    SVG_CACHE.set(url, promise);
    return promise;
  }

  function computePathLength(node) {
    if (!(node instanceof SVGGeometryElement)) return 0;
    try {
      const length = node.getTotalLength();
      return Number.isFinite(length) ? length : 0;
    } catch (error) {
      return 0;
    }
  }

  function prepareDrawable(node, style, strokeColor) {
    const length = computePathLength(node);
    if (length <= 0) return null;

    const computedStroke = node.getAttribute('stroke');
    const computedFill = node.getAttribute('fill');
    const fillOpacityAttr = node.getAttribute('fill-opacity');
    const strokeWidthAttr = node.getAttribute('stroke-width');
    const hasStroke = computedStroke && computedStroke !== 'none';
    const hasFill = computedFill && computedFill !== 'none';
    const resolvedStrokeColor = strokeColor || (hasStroke ? computedStroke : hasFill ? computedFill : '#111');

    node.style.transition = 'none';
    node.style.strokeDasharray = `${length}`;
    node.style.strokeDashoffset = `${length}`;
    node.style.strokeLinecap = node.style.strokeLinecap || 'round';
    node.style.strokeLinejoin = node.style.strokeLinejoin || 'round';

    let revealFill = false;

    if (!hasStroke && hasFill) {
      node.style.stroke = resolvedStrokeColor;
      node.style.strokeWidth = strokeWidthAttr || (style === 'classic' ? '2' : '1.6');
      revealFill = true;
    } else if (hasStroke) {
      node.style.stroke = resolvedStrokeColor;
      node.style.strokeWidth = strokeWidthAttr || node.style.strokeWidth || '2';
      revealFill = hasFill;
    } else {
      node.style.stroke = resolvedStrokeColor;
      node.style.strokeWidth = style === 'classic' ? '2' : '1.6';
    }

    if (revealFill) {
      const fillOpacity = fillOpacityAttr || '1';
      node.dataset.originalFillOpacity = fillOpacity;
      node.style.fillOpacity = '0';
      if (strokeColor) {
        node.style.fill = strokeColor;
      }
    } else if (!hasFill) {
      node.style.fill = 'none';
    } else if (strokeColor) {
      node.style.fill = strokeColor;
    }

    return {
      node,
      length,
      revealFill,
    };
  }

  function chooseEdgePlacement(containerRect, doodleWidth, doodleHeight, existingRects, config) {
    const sideChoices = ['left', 'right', 'top', 'bottom'];
    const marginX = SAFE_MARGIN_PX;
    const marginY = SAFE_MARGIN_PX;

    for (let i = 0; i < 30; i += 1) {
      const side = pickRandom(sideChoices);
      let nx = 0.5;
      let ny = 0.5;

      if (side === 'left') {
        nx = rand(0.03, EDGE_BAND);
        ny = rand(0.08, 0.92);
      } else if (side === 'right') {
        nx = rand(1 - EDGE_BAND, 0.97);
        ny = rand(0.08, 0.92);
      } else if (side === 'top') {
        nx = rand(0.08, 0.92);
        ny = rand(0.03, EDGE_BAND);
      } else {
        nx = rand(0.08, 0.92);
        ny = rand(1 - EDGE_BAND, 0.97);
      }

      if (
        nx > CENTER_GUARD.minX &&
        nx < CENTER_GUARD.maxX &&
        ny > CENTER_GUARD.minY &&
        ny < CENTER_GUARD.maxY
      ) {
        continue;
      }

      const rawX = nx * containerRect.width - doodleWidth / 2;
      const rawY = ny * containerRect.height - doodleHeight / 2;

      const x = clamp(rawX, marginX, containerRect.width - doodleWidth - marginX);
      const y = clamp(rawY, marginY, containerRect.height - doodleHeight - marginY);

      const rect = {
        x,
        y,
        width: doodleWidth,
        height: doodleHeight,
      };

      const overlaps = existingRects.some((existing) => {
        const gap = config?.overlapGapPx || 0;

        if (config?.strictNoOverlap) {
          // Expand both rectangles by the configured gap and disallow any intersection.
          const ax1 = rect.x - gap;
          const ay1 = rect.y - gap;
          const ax2 = rect.x + rect.width + gap;
          const ay2 = rect.y + rect.height + gap;

          const bx1 = existing.x - gap;
          const by1 = existing.y - gap;
          const bx2 = existing.x + existing.width + gap;
          const by2 = existing.y + existing.height + gap;

          return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1;
        }

        const overlapX = Math.max(
          0,
          Math.min(rect.x + rect.width, existing.x + existing.width) - Math.max(rect.x, existing.x),
        );
        const overlapY = Math.max(
          0,
          Math.min(rect.y + rect.height, existing.y + existing.height) - Math.max(rect.y, existing.y),
        );
        const overlapArea = overlapX * overlapY;
        if (overlapArea <= 0) return false;
        const minArea = Math.min(rect.width * rect.height, existing.width * existing.height);
        return overlapArea / minArea > 0.22;
      });

      if (!overlaps) {
        return rect;
      }
    }

    return null;
  }

  function getAssetUrls(container, style) {
    const nodes = Array.from(container.querySelectorAll(ASSET_SELECTOR));
    return nodes
      .filter((node) => node.dataset.doodleFor === style)
      .map((node) => node.dataset.doodleAssetUrl)
      .filter(Boolean);
  }

  function createWrapper() {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.opacity = '0';
    wrapper.style.willChange = 'transform, opacity';
    wrapper.style.transform = 'translate3d(0, 0, 0) rotate(var(--doodle-rotation, 0deg))';
    return wrapper;
  }

  // `isDestroyedFn` is a zero-argument callback that returns true when the
  // owning DoodleManager has been destroyed.  Checking it at each await point
  // prevents a destroyed manager from continuing to animate SVG paths — the
  // original loop had no such guard, so destroying a manager mid-animation
  // would still run all remaining path animations to completion.
  async function animateSvgInWrapper(svg, wrapper, style, config, isDestroyedFn, strokeColor) {
    const svgClone = svg.cloneNode(true);
    const drawables = Array.from(svgClone.querySelectorAll(DRAWABLE_SELECTOR))
      .map((node) => prepareDrawable(node, style, strokeColor))
      .filter(Boolean);

    if (!drawables.length) {
      return false;
    }

    svgClone.style.width = '100%';
    svgClone.style.height = '100%';
    svgClone.style.display = 'block';
    svgClone.style.overflow = 'visible';

    wrapper.appendChild(svgClone);
    wrapper.style.opacity = '1';

    const perPathMs = config.drawMsPerPath;
    const drawDurationMs = clamp(
      drawables.length * perPathMs,
      config.minDrawMs,
      config.maxDrawMs,
    );
    const segmentMs = drawDurationMs / drawables.length;

    for (let i = 0; i < drawables.length; i += 1) {
      // If the manager was destroyed mid-animation, bail out immediately
      // instead of continuing to draw the remaining paths.
      if (isDestroyedFn()) return false;

      const item = drawables[i];
      const path = item.node;

      path.animate(
        [
          { strokeDashoffset: `${item.length}` },
          { strokeDashoffset: '0' },
        ],
        {
          duration: segmentMs,
          easing: 'linear',
          fill: 'forwards',
        },
      );

      await new Promise((resolve) => setTimeout(resolve, segmentMs));

      if (item.revealFill) {
        const fillOpacity = path.dataset.originalFillOpacity || '1';
        path.animate(
          [{ fillOpacity: '0' }, { fillOpacity }],
          {
            duration: 180,
            easing: 'ease-out',
            fill: 'forwards',
          },
        );
        path.style.fillOpacity = fillOpacity;
      }
    }

    if (isDestroyedFn()) return false;

    await new Promise((resolve) => setTimeout(resolve, config.holdMs));

    if (isDestroyedFn()) return false;

    await wrapper
      .animate(
        [
          { opacity: 1, transform: 'translate3d(0, 0, 0) rotate(var(--doodle-rotation, 0deg)) scale(1)' },
          { opacity: 0, transform: 'translate3d(0, 0, 0) rotate(var(--doodle-rotation, 0deg)) scale(0.97)' },
        ],
        {
          duration: config.fadeMs,
          easing: 'ease-out',
          fill: 'forwards',
        },
      )
      .finished
      .catch(() => {});

    return true;
  }

  function getViewBoxDimensions(svg) {
    const viewBox = svg.viewBox && svg.viewBox.baseVal;
    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      return { width: viewBox.width, height: viewBox.height };
    }

    const widthAttr = Number(svg.getAttribute('width')) || 120;
    const heightAttr = Number(svg.getAttribute('height')) || 120;
    return { width: widthAttr, height: heightAttr };
  }

  class DoodleManager {
    constructor(container) {
      this.container = container;
      this.style = container.dataset.doodleStyle || 'edge-sketch';
      this.config = getStyleConfig(this.style);
      this.strokeColor = getDoodleStrokeColor(container, this.style);
      this.assetUrls = getAssetUrls(container, this.style);
      this.active = [];
      this.pendingSpawns = 0;
      this.isDestroyed = false;
      this.pendingTimer = null;
      this.isTicking = false;
    }

    start() {
      if (!this.assetUrls.length || this.isDestroyed) return;
      this.scheduleNext(120);
    }

    destroy() {
      this.isDestroyed = true;
      if (this.pendingTimer) {
        clearTimeout(this.pendingTimer);
      }
      this.pendingTimer = null;
      this.active.forEach((item) => {
        if (item.wrapper && item.wrapper.parentNode) {
          item.wrapper.remove();
        }
      });
      this.active = [];
      this.pendingSpawns = 0;
    }

    scheduleNext(delayMs) {
      if (this.isDestroyed) return;
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.tick(), delayMs);
    }

    getCurrentMaxActive() {
      const mobileMax = this.config.maxActiveMobile || this.config.maxActive;
      return IS_MOBILE_MQ.matches ? mobileMax : this.config.maxActive;
    }

    async tick() {
      if (this.isDestroyed || this.isTicking) return;

      this.isTicking = true;
      this.pendingTimer = null;

      try {
        this.active = this.active.filter((item) => item.expiresAt > Date.now());
        const maxActive = this.getCurrentMaxActive();

        if (this.active.length + this.pendingSpawns < maxActive) {
          this.spawnOne().catch(() => {});
        }
      } finally {
        this.isTicking = false;
        const nextDelay = rand(this.config.minGapMs, this.config.maxGapMs);
        this.scheduleNext(nextDelay);
      }
    }

    async spawnOne() {
      this.pendingSpawns += 1;

      try {
        const containerRect = this.container.getBoundingClientRect();
        if (containerRect.width < 120 || containerRect.height < 120) return;

        const assetUrl = pickRandom(this.assetUrls);
        const svg = await loadSvg(assetUrl);
        if (!svg || this.isDestroyed) return;
        const assetOverride = getClassicAssetOverride(this.style, assetUrl);

        const { width: sourceW, height: sourceH } = getViewBoxDimensions(svg);
        const scale = rand(this.config.minScale, this.config.maxScale);
        let doodleWidth = sourceW * scale;
        let doodleHeight = sourceH * scale;

        if (assetOverride?.sizeMultiplier) {
          doodleWidth *= assetOverride.sizeMultiplier;
          doodleHeight *= assetOverride.sizeMultiplier;
        }

        if (IS_MOBILE_MQ.matches) {
          const mobileWidth = clamp(
            doodleWidth,
            MOBILE_DOODLE_MIN_WIDTH_PX,
            MOBILE_DOODLE_MAX_WIDTH_PX,
          );
          const aspectRatio = sourceW > 0 ? sourceH / sourceW : 1;
          doodleWidth = mobileWidth;
          doodleHeight = mobileWidth * aspectRatio;
        }

        const placement = chooseEdgePlacement(containerRect, doodleWidth, doodleHeight, this.active, this.config);
        if (!placement) return;

        const wrapper = createWrapper();
        wrapper.style.width = `${placement.width}px`;
        wrapper.style.height = `${placement.height}px`;
        wrapper.style.left = `${placement.x}px`;
        wrapper.style.top = `${placement.y}px`;
        if (assetOverride) {
          const rotation = rand(assetOverride.minRotationDeg, assetOverride.maxRotationDeg);
          wrapper.style.setProperty('--doodle-rotation', `${rotation}deg`);
        }

        this.container.appendChild(wrapper);

        const expiresAt = Date.now() + this.config.maxDrawMs + this.config.holdMs + this.config.fadeMs + 1200;
        this.active.push({
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        wrapper,
        expiresAt,
        });

        const didAnimate = await animateSvgInWrapper(
          svg,
          wrapper,
          this.style,
          this.config,
          () => this.isDestroyed,
          this.strokeColor,
        );
        if (didAnimate && wrapper.parentNode) {
          wrapper.remove();
        }

        this.active = this.active.filter((item) => item.wrapper !== wrapper);
      } finally {
        this.pendingSpawns = Math.max(0, this.pendingSpawns - 1);
      }
    }
  }

  function initContainer(container) {
    const existing = doodleManagers.get(container);
    if (existing) {
      existing.destroy();
    }

    const manager = new DoodleManager(container);
    doodleManagers.set(container, manager);
    manager.start();
  }

  function initAll(scope = document) {
    const containers = scope.querySelectorAll(SELECTOR);
    containers.forEach((container) => initContainer(container));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
